<script setup lang="ts">
type Props = {
  reward: Reward;
  buttonVisible?: boolean;
  statusVisible?: boolean;
  bigCard?: boolean;
};

const {
  reward,
  buttonVisible = true,
  statusVisible = true,
  bigCard = false,
} = defineProps<Props>();

type Emits = {
  (e: 'redeem'): void;
};

const emit = defineEmits<Emits>();

const { showMoneyFormat } = useWallet();
const { getDateTime } = useDateTimeHelper();

const countDownTotalTime = computed(() => {
  const now = new Date();
  const expireDatetime = new Date(reward.expires_at);

  return expireDatetime.getTime() > now.getTime()
    ? (expireDatetime.getTime() - now.getTime()) / 1000
    : 0;
});

const isCountDownFinished = ref<boolean>(false);

const rewardIsExpired = computed<boolean>(() => {
  if (isCountDownFinished.value) {
    return true;
  }

  return new Date(reward.expires_at) < new Date();
});

const isButtonVisible = computed<boolean>(() => {
  if (!buttonVisible || reward.status !== 'available') {
    return false;
  }

  return true;
});

function onRewardExpired() {
  isCountDownFinished.value = true;
}

function getRewardIcon(reward: Reward) {
  if (reward.type === 'money_real') {
    return 'user/wallet';
  }

  if (reward.type === 'money_bonus') {
    return 'koin';
  }

  if (reward.type === 'money_cashback') {
    return 'user/cashback';
  }

  if (reward.type === 'freespins') {
    return 'cards';
  }

  if (reward.type === 'freebets') {
    return 'futbol';
  }

  return 'user/rewards';
}

function getRewardStatus(status: RewardStatus) {
  if (status === 'available') {
    return {
      text: 'Disponível',
      color: 'text-primary',
      bg: 'bg-primary/5',
      border: 'border-primary/10',
      icon: 'clock',
    };
  }

  if (status === 'redeemed') {
    return {
      text: 'Resgatado',
      color: 'text-success',
      bg: 'bg-success/5',
      border: 'border-success/10',
      icon: 'check',
    };
  }

  if (status === 'auto_redeemed') {
    return {
      text: 'Resgate automático',
      color: 'text-success',
      bg: 'bg-success/5',
      border: 'border-success/10',
      icon: 'check',
    };
  }

  if (status === 'expired') {
    return {
      text: 'Expirado',
      color: 'text-error',
      bg: 'bg-error/5',
      border: 'border-error/10',
      icon: 'timer',
    };
  }

  if (status === 'canceled') {
    return {
      text: 'Cancelado',
      color: 'text-error',
      bg: 'bg-error/5',
      border: 'border-error/10',
      icon: 'close',
    };
  }

  return {
    text: status,
    color: 'text-warning',
    bg: 'bg-warning/5',
    border: 'border-warning/10',
    icon: 'info',
  };
}

const { normalizeThumbnail, getThumbnailPathSet } = useCasino();

const thumbnailPathSet = computed<string>(() => {
  if (!reward.game_image) {
    return '';
  }

  return getThumbnailPathSet(reward.game_image);
});

const thumbnailPath = computed<string>(() => {
  if (!reward.game_image) {
    return '';
  }

  return `${normalizeThumbnail(reward.game_image)}/public`;
});

const extractedValue = computed<string | null>(() => {
  const match = reward.player_desc?.match(/{([^}]+)}/);

  return match?.[1]?.trim() ?? null;
});

const displayDescription = computed<string>(() => {
  return reward.player_desc?.replace(/{[^}]+}/, '').trim() || '';
});

const isCasinoGame = computed<boolean>(() => {
  return !!reward.game_slug_url;
});

const isSportEvent = computed<boolean>(() => {
  return reward.type === 'freebets';
});

const linkTo = computed<string>(() => {
  if (isCasinoGame.value) {
    const slug = reward.game_slug_url
      ? `/games/${reward.game_slug_url.toLowerCase()}`
      : '/';

    return localeLink(slug);
  }

  if (isSportEvent.value) {
    return localeLink(`/sports`);
  }

  return '';
});
</script>

<template>
  <div :class="[$style.rewardsItem, { [$style.bigCard]: bigCard }]">
    <div :class="$style.rewardsCol">
      <NuxtLink
        v-tooltip.right="`ID: #${reward.id}`"
        :class="$style.rewardsItemImage"
        :to="linkTo"
      >
        <img
          v-if="thumbnailPath"
          :alt="reward.player_desc"
          :src="thumbnailPath"
          :srcset="thumbnailPathSet || undefined"
        />

        <div :class="$style.rewardsItemIcon">
          <NuxtIcon :name="getRewardIcon(reward)" />
        </div>
      </NuxtLink>

      <div :class="$style.rewardsItemInfo">
        <span :class="$style.rewardsItemName">{{ displayDescription }}</span>

        <div :class="$style.rewardsItemValue">
          <span v-if="extractedValue"> Valor: {{ extractedValue }} </span>
          <span v-else-if="reward.amount">
            Valor: {{ showMoneyFormat({ value: reward.amount / 100 }) }}
          </span>

          <span
            v-if="(extractedValue || reward.amount) && reward.quantity"
            :class="$style.rewardsItemSeparator"
          />

          <span v-if="reward.quantity">
            Quantidade: {{ reward.quantity }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="isButtonVisible || statusVisible"
      :class="$style.rewardsCol"
    >
      <div
        v-if="isButtonVisible"
        :class="$style.rewardsItemButtons"
      >
        <div
          :class="[
            $style.rewardsCountdown,
            {
              [$style.isExpired]: rewardIsExpired,
            },
          ]"
        >
          <div
            v-if="!rewardIsExpired"
            :class="$style.countdownTitle"
          >
            Expira em:
          </div>

          <WidgetsCountdown
            v-if="countDownTotalTime && !rewardIsExpired"
            :total-time="countDownTotalTime"
            @timer-finished="onRewardExpired"
          >
            <template #process="{ time }">
              <span :class="$style.countdownTime">
                {{ `${time.hours}h : ${time.minutes}m : ${time.seconds}s` }}
              </span>
            </template>
            <template #finish>
              <span :class="$style.countdownTime">
                {{ $t('rewards.expired') }}
              </span>
            </template>
          </WidgetsCountdown>

          <template v-else>
            <span :class="$style.countdownTime">
              {{ $t('rewards.expired') }}
            </span>
          </template>
        </div>

        <button
          :class="$style.rewardsItemButton"
          :disabled="rewardIsExpired"
          @click="emit('redeem')"
        >
          Resgatar
        </button>
      </div>

      <div
        v-else-if="statusVisible"
        :class="[
          $style.rewardsItemStatus,
          getRewardStatus(reward.status).color,
          getRewardStatus(reward.status).bg,
          getRewardStatus(reward.status).border,
        ]"
      >
        <span :class="$style.rewardsItemStatusText">
          <NuxtIcon
            :class="[
              $style.rewardsItemStatusIcon,
              getRewardStatus(reward.status).color,
            ]"
            :name="getRewardStatus(reward.status).icon"
          />

          {{ getRewardStatus(reward.status).text }}
        </span>

        <span
          v-if="reward.updated_at"
          :class="$style.rewardsItemUpdatedAt"
        >
          {{ getDateTime(reward.updated_at) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style module lang="postcss">
.rewardsItem {
  @apply flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4 p-3 md:p-4 bg-texts/5 border border-solid border-texts/10 rounded-lg;

  .rewardsCol {
    @apply flex items-center justify-between gap-4;
  }

  .rewardsItemImage {
    @apply min-w-12 w-12 h-12 rounded-lg overflow-hidden select-none;

    img {
      @apply w-full h-full object-cover;
    }

    .rewardsItemIcon {
      @apply text-2xl text-primary w-full h-full bg-primary/10 flex items-center justify-center border border-solid border-primary/20 rounded-lg;
    }
  }

  .rewardsItemInfo {
    @apply flex flex-col mr-auto;

    .rewardsItemName {
      @apply text-sm text-texts font-semibold;
    }

    .rewardsItemValue {
      @apply text-sm text-texts/70 flex items-center;

      .rewardsItemSeparator {
        @apply w-px h-4 bg-texts/20 mx-2;
      }
    }
  }

  .rewardsItemButtons {
    @apply flex items-stretch gap-2 select-none;

    .rewardsCountdown {
      @apply text-xs leading-none text-texts/70 w-36 bg-texts/10 rounded-md flex flex-col items-center justify-center py-1.5;

      .countdownTitle {
        @apply text-2xs text-texts/50 font-medium;
      }

      .countdownTime {
        @apply text-xs text-texts/70 font-semibold;
      }

      &.isExpired {
        @apply bg-error/20 text-white;
      }
    }

    .rewardsItemButton {
      @apply text-sm bg-button-bg text-button-text px-4 py-2 rounded-md cursor-pointer font-bold;

      &:disabled {
        @apply opacity-50 cursor-not-allowed;
      }
    }
  }

  .rewardsItemStatus {
    @apply flex items-stretch flex-col select-none py-1.5 px-3 rounded-md border border-solid min-w-44;

    .rewardsItemStatusText {
      @apply text-xs flex items-center gap-1 font-semibold;

      .rewardsItemStatusIcon {
        @apply text-xs text-texts/70;
      }
    }

    .rewardsItemUpdatedAt {
      @apply text-2xs font-medium opacity-70;
    }
  }

  &.bigCard {
    @apply flex-col items-center p-6 text-center;

    .rewardsCol {
      @apply flex-col items-center;
    }

    .rewardsItemImage {
      @apply min-w-24 w-24 h-24 rounded-xl;

      .rewardsItemIcon {
        @apply text-4xl rounded-xl;
      }
    }

    .rewardsItemInfo {
      @apply mr-0 -mb-1;

      .rewardsItemName {
        @apply text-base;
      }

      .rewardsItemValue {
        @apply justify-center;
      }
    }
  }
}
</style>
